import type { FC, ReactNode } from 'react';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Badge,
  Box,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DeleteDialog,
  Iconify,
  MenuItem,
  MoneyInput,
  TableMoreMenu,
} from '@pflegenavi/web-components';
import type {
  BatchReceiptDetails,
  ReceiptBatchFormValues,
} from './ReceiptBatchForm';
import type { FormikErrors, FormikHelpers, FormikTouched } from 'formik';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { DatePicker } from '@mui/x-date-pickers';
import { fromZonedTime } from 'date-fns-tz';
import { startOfDay } from 'date-fns';
import { MAX_ENTRY_TOTAL_AMOUNT_FOR_RECEIPT_GENERATION_IN_CENTS } from '@pflegenavi/shared/constants';
import { useBufferedField } from '../hooks/useBufferedField';

interface ReceiptBatchRowProps {
  index: number;
  receipt: BatchReceiptDetails;
  individualDatesMode?: boolean;
  setReceiptFieldValue: (field: string, value: any) => void;
  removeRow: (index: number) => void;
  SelectResidents: ReactNode;
  errors: FormikErrors<BatchReceiptDetails> | undefined;
  touched: FormikTouched<BatchReceiptDetails> | undefined;
  setFieldTouched?: FormikHelpers<ReceiptBatchFormValues>['setFieldTouched'];
  setShownImageId: (imgId: string | undefined) => void;
  setPreviewImageIds: (imgIds: string[]) => void;
  // Invoked when Tab is pressed on the note input of the last row, appending
  // a new row. Only wired up when isLastRow is true.
  onTabAddRow: () => void;
  isLastRow: boolean;
  showTabTooltip: boolean;
  setShowTabTooltip: React.Dispatch<React.SetStateAction<boolean>>;
  upload: (file: File) => Promise<string | undefined>;
  addImages: (imageIds: string[]) => void;
  handleEntryRecurringItemCalculation?: (
    entryId: string,
    index: number,
    amount: number
  ) => void;
  autoGeneratedReceipts: boolean;
  onAmountBlur?: (index: number, latestAmount: number | undefined) => void;
  showAdditionalFees: boolean;
  showIncludedFees: boolean;
}

const rowHeight = 60;

const ACCEPTED_FILE_TYPES = 'image/jpeg,image/png,application/pdf';

export const ReceiptBatchRowView: FC<ReceiptBatchRowProps> = memo(
  ({
    index,
    receipt,
    individualDatesMode,
    setReceiptFieldValue,
    removeRow,
    SelectResidents,
    errors,
    touched,
    setFieldTouched,
    setShownImageId,
    setPreviewImageIds,
    onTabAddRow,
    isLastRow,
    showTabTooltip: showTabTooltipOriginal,
    setShowTabTooltip,
    upload,
    addImages,
    handleEntryRecurringItemCalculation,
    autoGeneratedReceipts,
    onAmountBlur,
    showAdditionalFees,
    showIncludedFees,
  }) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const individualDatesModeEnabled = individualDatesMode;
    const showAdditionalFee =
      Boolean(receipt.additionalFeeAmount) || showAdditionalFees;
    const showIncludedFee =
      Boolean(receipt.includedFeeAmount) || showIncludedFees;

    let noteFieldWidth = 525;
    let amountFieldWidth = 110;
    const datePickerFieldWidth = 140;

    if (individualDatesModeEnabled) {
      noteFieldWidth -= datePickerFieldWidth;
    }

    if (showAdditionalFee) {
      amountFieldWidth += 75;
      noteFieldWidth -= 75;
    }

    if (showIncludedFee) {
      amountFieldWidth += 75;
      noteFieldWidth -= 75;
    }

    const [openMenu, setOpenMenuActions] = useState<HTMLElement | null>(null);

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    const [deleteImagesModalOpen, setDeleteImagesModalOpen] = useState(false);

    const inputRef = useRef<HTMLInputElement | null>(null);
    const noteInputRef = useRef<HTMLInputElement | null>(null);

    // Tab on the last note input appends a new row. Attached via the native
    // DOM API (not React's onKeyDown) because MUI's TextField does not forward
    // a top-level onKeyDown to the inner input element reliably enough.
    useEffect(() => {
      const inputEl = noteInputRef.current;
      if (!inputEl || !isLastRow) {
        return undefined;
      }
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Tab' && !e.shiftKey) {
          e.preventDefault();
          onTabAddRow();
        }
      };
      inputEl.addEventListener('keydown', handleKeyDown);
      return () => {
        inputEl.removeEventListener('keydown', handleKeyDown);
      };
    }, [isLastRow, onTabAddRow]);

    const { enqueueSnackbar } = useSnackbar();

    const handleFileChange = useCallback(
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const acceptedFileTypeRegex = ACCEPTED_FILE_TYPES.split(',').join('|');
        const file = e.target.files?.[0];
        if (file) {
          if (file.type.match(acceptedFileTypeRegex)) {
            setOpenMenuActions(null);
            const imageId = await upload(file);
            imageId && addImages([imageId]);
          } else {
            setOpenMenuActions(null);
            enqueueSnackbar(t('receipts.batch.invalid-file-type'), {
              variant: 'error',
            });
          }
        }
      },
      [t, enqueueSnackbar, upload, addImages]
    );

    const openFileInput = useCallback(() => {
      if (inputRef.current) {
        inputRef.current.click();
      }
    }, [inputRef]);

    // Stable callbacks so useBufferedField's handleBlur identity only changes
    // when externalValue/localValue change, not on every parent render.
    const setNote = useCallback(
      (notes: string | undefined) => {
        setReceiptFieldValue('notes', notes);
      },
      [setReceiptFieldValue]
    );

    const setIndividualDate = (date: Date | undefined) => {
      setReceiptFieldValue(
        'individualDate',
        date ? fromZonedTime(startOfDay(date), 'UTC') : undefined
      );
    };

    const handleDeleteRow = useCallback(() => {
      removeRow(index);
      setDeleteModalOpen(false);
      setOpenMenuActions(null);
    }, [index, removeRow]);

    const handleDeleteImages = useCallback(() => {
      setReceiptFieldValue('receiptImageIds', null);
      setDeleteImagesModalOpen(false);
      setOpenMenuActions(null);
    }, [setReceiptFieldValue]);

    const showAmountError = useMemo(() => {
      return Boolean(errors?.amount) && touched?.amount !== undefined;
    }, [errors?.amount, touched?.amount]);

    const showNoteError = useMemo(() => {
      return Boolean(errors?.notes) && touched?.notes !== undefined;
    }, [errors?.notes, touched?.notes]);

    const showIndividualDateError = useMemo(() => {
      return (
        Boolean(errors?.individualDate) && touched?.individualDate !== undefined
      );
    }, [errors?.individualDate, touched?.individualDate]);

    const handleClickOnAmountOrNotes = useCallback(() => {
      if (!receipt.id) {
        throw new Error('Entry ID is missing');
      }
      handleEntryRecurringItemCalculation?.(
        receipt.id,
        index,
        (receipt.amount ?? 0) * 100
      );
    }, [
      receipt.id,
      receipt.amount,
      handleEntryRecurringItemCalculation,
      index,
    ]);

    const onFocusRecurringItemAmountOrNotes = useCallback(
      (
        e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>
      ) => {
        e.preventDefault();
        e.target.blur();
      },
      []
    );

    const showTabTooltip = handleEntryRecurringItemCalculation
      ? false
      : showTabTooltipOriginal;

    const disableAutoGeneratedReceiptIcon = useMemo(() => {
      return (
        receipt.amount &&
        receipt.amount > MAX_ENTRY_TOTAL_AMOUNT_FOR_RECEIPT_GENERATION_IN_CENTS
      );
    }, [receipt.amount]);

    // Commit via onAmountBlur so the redistribution's setValues is the sole
    // writer; a separate setFieldValue would be clobbered by the later
    // setValues, which Formik resolves against stale closure state.
    const commitAmount = useCallback(
      (latestAmount: number | undefined) => {
        onAmountBlur?.(index, latestAmount);
      },
      [index, onAmountBlur]
    );

    const markAmountTouched = useCallback(() => {
      setFieldTouched?.(`receipts.${index}.amount`);
    }, [setFieldTouched, index]);

    const commitNote = useCallback(
      (value: string | undefined) => {
        setNote(value === '' ? undefined : value);
      },
      [setNote]
    );

    const markNoteTouched = useCallback(() => {
      setFieldTouched?.(`receipts.${index}.notes`);
    }, [setFieldTouched, index]);

    const recurringFocusOverride = handleEntryRecurringItemCalculation
      ? onFocusRecurringItemAmountOrNotes
      : undefined;

    const amountField = useBufferedField<number | undefined>(
      receipt.amount,
      commitAmount,
      markAmountTouched,
      recurringFocusOverride
    );

    const noteField = useBufferedField<string | undefined>(
      receipt.notes,
      commitNote,
      markNoteTouched,
      recurringFocusOverride
    );

    return (
      <>
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
          sx={{
            width: '100%',
            mb: 1,
            mt: index === 0 ? 0 : 2.4,
            minHeight: rowHeight,
          }}
        >
          <Stack
            justifyContent="center"
            alignItems="center"
            sx={{
              minWidth: 30,
              height: rowHeight - 12,
            }}
          >
            <Typography fontWeight={700} fontSize={18}>
              #{index + 1}
            </Typography>
          </Stack>

          <Box sx={{ width: 300 }}>
            <TooltipWrap
              setShowTabTooltip={setShowTabTooltip}
              showTabTooltip={showTabTooltipOriginal}
            >
              <div>{SelectResidents}</div>
            </TooltipWrap>
          </Box>
          {individualDatesModeEnabled && (
            <Box
              sx={{
                width: datePickerFieldWidth,
                minWidth: datePickerFieldWidth,
              }}
            >
              <DatePicker
                label={t('common.date')}
                value={receipt.individualDate ?? null}
                maxDate={new Date()}
                onChange={(value) => {
                  setIndividualDate(value ?? undefined);
                }}
                slotProps={{
                  textField: {
                    name: `receipts.${index}.individualDate`,
                    variant: 'standard',
                    InputLabelProps: {
                      shrink: true,
                    },
                    inputProps: {
                      placeholder: '',
                    },
                    onBlur: () => {
                      setFieldTouched?.(
                        `receipts.${index}.individualDate`,
                        true
                      );
                    },
                    error: showIndividualDateError,
                    helperText: showIndividualDateError
                      ? errors?.individualDate
                      : undefined,
                  },
                }}
              />
            </Box>
          )}
          <TooltipWrap
            setShowTabTooltip={setShowTabTooltip}
            showTabTooltip={showTabTooltip}
          >
            <div>
              <Stack
                direction={'row'}
                gap={1}
                width={amountFieldWidth}
                minWidth={amountFieldWidth}
              >
                <MoneyInput
                  name={`receipts.${index}.amount`}
                  variant="standard"
                  label={t('common.amount')}
                  value={amountField.localValue}
                  onChange={amountField.setLocalValue}
                  onWheel={(e) => (e.target as HTMLElement).blur()}
                  sx={{ flex: 6 }}
                  onBlur={amountField.handleBlur}
                  onFocus={amountField.handleFocus}
                  error={showAmountError}
                  helperText={showAmountError ? errors?.amount : undefined}
                  InputProps={{
                    readOnly: !!handleEntryRecurringItemCalculation,
                  }}
                  onClick={
                    handleEntryRecurringItemCalculation
                      ? handleClickOnAmountOrNotes
                      : undefined
                  }
                />
                {showIncludedFee && (
                  <MoneyInput
                    name={`receipts.${index}.included-fee-amount`}
                    variant="standard"
                    label={t('common.included-fee-amount-short')}
                    value={receipt.includedFeeAmount}
                    sx={{
                      flex: 4,
                    }}
                    disabled={true}
                  />
                )}
                {showAdditionalFee && (
                  <MoneyInput
                    name={`receipts.${index}.additional-fee-amount`}
                    variant="standard"
                    label={t('common.additional-fee-amount-short')}
                    value={receipt.additionalFeeAmount}
                    sx={{
                      flex: 4,
                    }}
                    disabled={true}
                  />
                )}
              </Stack>
            </div>
          </TooltipWrap>

          <TooltipWrap
            setShowTabTooltip={setShowTabTooltip}
            showTabTooltip={showTabTooltip}
          >
            <TextField
              label={t('receipts.batch.form.note')}
              name={`receipts.${index}.notes`}
              value={noteField.localValue ?? ''}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => noteField.setLocalValue(e.target.value)}
              onBlur={noteField.handleBlur}
              onFocus={noteField.handleFocus}
              variant="standard"
              sx={{
                width: noteFieldWidth,
              }}
              error={showNoteError}
              helperText={showNoteError ? errors?.notes : undefined}
              inputRef={noteInputRef}
              InputProps={{
                readOnly: !!handleEntryRecurringItemCalculation,
              }}
              onClick={
                handleEntryRecurringItemCalculation
                  ? handleClickOnAmountOrNotes
                  : undefined
              }
            />
          </TooltipWrap>

          <Stack
            justifyContent="center"
            alignItems="center"
            direction="row"
            spacing={1}
            sx={{ height: rowHeight - 12 }}
          >
            {receipt.receiptImageIds && receipt.receiptImageIds.length > 0 ? (
              <Tooltip title={t('receipts.batch.form.receipt-image-available')}>
                <IconButton
                  tabIndex={-1}
                  onClick={() => {
                    setPreviewImageIds(receipt.receiptImageIds ?? []);
                    setShownImageId(
                      receipt.receiptImageIds
                        ? receipt.receiptImageIds[0]
                        : undefined
                    );
                  }}
                >
                  <Badge
                    badgeContent={receipt.receiptImageIds.length}
                    invisible={receipt.receiptImageIds.length < 2}
                    color="info"
                    sx={{
                      '& .MuiBadge-badge': { fontSize: 12 },
                      position: 'absolute',
                      right: 13,
                      bottom: 11,
                    }}
                  />
                  <Iconify
                    icon="mdi:receipt-text"
                    color={theme.palette.success.main}
                    fontSize={35}
                  />
                </IconButton>
              </Tooltip>
            ) : autoGeneratedReceipts ? (
              <Tooltip
                title={t('receipts.batch.one-receipt-will-be-auto-generated')}
              >
                <IconButton
                  tabIndex={-1}
                  disabled={Boolean(disableAutoGeneratedReceiptIcon)}
                >
                  <Iconify
                    icon="mdi:receipt-text"
                    color={
                      disableAutoGeneratedReceiptIcon
                        ? theme.palette.grey['400']
                        : theme.palette.info.main
                    }
                    fontSize={35}
                  />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip
                title={t('receipts.batch.form.receipt-image-unavailable')}
              >
                <IconButton tabIndex={-1}>
                  <Iconify
                    icon="mdi:receipt-text-remove"
                    color={theme.palette.grey['400']}
                    fontSize={35}
                  />
                </IconButton>
              </Tooltip>
            )}

            <input
              type="file"
              accept={ACCEPTED_FILE_TYPES}
              ref={inputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
              tabIndex={-1}
            />
            <TableMoreMenu
              iconButtonProps={{
                tabIndex: -1,
              }}
              open={openMenu}
              onOpen={() =>
                setOpenMenuActions(document.activeElement as HTMLElement)
              }
              onClose={() => setOpenMenuActions(null)}
              actions={
                <>
                  <MenuItem onClick={() => openFileInput()}>
                    <Iconify icon={'ri:image-add-line'} />{' '}
                    {t('receipts.batch.form.add-image')}
                  </MenuItem>
                  <MenuItem
                    sx={{ color: 'error.main' }}
                    onClick={() => setDeleteImagesModalOpen(true)}
                    disabled={!receipt.receiptImageIds?.length}
                  >
                    <Iconify icon={'mdi:receipt-text-remove-outline'} />{' '}
                    {t('receipts.batch.form.delete-images')}
                  </MenuItem>
                  <MenuItem
                    sx={{ color: 'error.main' }}
                    onClick={() => setDeleteModalOpen(true)}
                  >
                    <Iconify icon={'eva:trash-2-outline'} />{' '}
                    {t('receipts.batch.form.delete-entry')}
                  </MenuItem>
                </>
              }
            />
          </Stack>

          <DeleteDialog
            open={deleteImagesModalOpen}
            handleDelete={handleDeleteImages}
            setDeleteDialogOpen={setDeleteImagesModalOpen}
            confirmationMessage={t(
              'receipts.batch.form.delete-images-confirmation-text'
            )}
          />
          <DeleteDialog
            open={deleteModalOpen}
            handleDelete={handleDeleteRow}
            setDeleteDialogOpen={setDeleteModalOpen}
            confirmationMessage={t(
              'receipts.batch.form.delete-confirmation-text'
            )}
          />
        </Stack>
        <Divider
          sx={{
            marginY: 3,
            borderStyle: 'dashed',
          }}
        />
      </>
    );
  }
);

interface TooltipWrapProps {
  children: React.ReactElement<any, any>;
  showTabTooltip: boolean;
  setShowTabTooltip: React.Dispatch<React.SetStateAction<boolean>>;
}

const TooltipWrap: FC<TooltipWrapProps> = ({
  children,
  showTabTooltip,
  setShowTabTooltip,
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <Tooltip
      title={t('receipts.batch.form.hint-tab')}
      disableHoverListener={true}
      disableTouchListener={true}
      disableFocusListener={true}
      onFocus={() => {
        if (showTabTooltip) {
          setOpen(true);
        }
      }}
      onBlur={() => {
        setShowTabTooltip(false);
        setOpen(false);
      }}
      arrow={true}
      placement="top"
      open={open}
      slotProps={{
        tooltip: {
          sx: {
            textAlign: 'center',
          },
        },
      }}
    >
      {children}
    </Tooltip>
  );
};
